import { useState, useMemo } from "react"
import { css, type SerializedStyles } from "@emotion/react"
import { sanitize } from "./utils"

const DEFAULT_CHARACTER_LIMIT = 150

function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html")
  return doc.body.textContent || ""
}

export function ExpandableDescription({
  text,
  isHtml,
  textStyle,
  characterLimit = DEFAULT_CHARACTER_LIMIT,
}: {
  text: string
  isHtml?: boolean
  textStyle?: SerializedStyles
  characterLimit?: number
}) {
  const [expanded, setExpanded] = useState(false)
  const plainText = useMemo(
    () => (isHtml ? stripHtml(text) : text),
    [text, isHtml],
  )
  const needsTruncation = plainText.length > characterLimit

  if (!needsTruncation) {
    if (isHtml) {
      return (
        <div
          css={[styles.text, styles.compactHtml, textStyle]}
          dangerouslySetInnerHTML={{ __html: sanitize(text) }}
        />
      )
    }

    return <p css={[styles.text, textStyle]}>{text}</p>
  }

  if (expanded) {
    return (
      <div css={[styles.text, textStyle]}>
        {isHtml ? (
          <div css={styles.expandedHtml}>
            <div
              data-expanded-html-description
              dangerouslySetInnerHTML={{ __html: sanitize(text) }}
            />{" "}
            <Trigger expanded onClick={() => setExpanded(false)} />
          </div>
        ) : (
          <p css={styles.expandedPlain}>
            {text} <Trigger expanded onClick={() => setExpanded(false)} />
          </p>
        )}
      </div>
    )
  }

  const truncated = plainText.slice(0, characterLimit).trimEnd()

  return (
    <p css={[styles.text, textStyle]}>
      {truncated}…{" "}
      <Trigger expanded={false} onClick={() => setExpanded(true)} />
    </p>
  )
}

function Trigger({
  expanded,
  onClick,
}: {
  expanded: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      css={styles.trigger}
      onClick={onClick}
    >
      {expanded ? "Read less" : "Read more"}
    </button>
  )
}

const styles = {
  text: css`
    margin: 0;
    color: var(--d-text-color-secondary);
  `,
  trigger: css`
    all: unset;
    font-size: inherit;
    font-weight: 600;
    color: var(--d-text-color-interaction);
    cursor: pointer;
    display: inline;
  `,
  compactHtml: css`
    > * {
      margin-top: 0;
      margin-bottom: 0;
    }
    > * + * {
      margin-top: 0.5rem;
    }
  `,
  expandedHtml: css`
    [data-expanded-html-description] > * {
      margin-top: 0;
      margin-bottom: 0;
    }
    [data-expanded-html-description] > * + * {
      margin-top: 0.5rem;
    }
    > [data-expanded-html-description],
    > [data-expanded-html-description] > :last-child {
      display: inline;
    }
  `,
  expandedPlain: css`
    margin: 0;
  `,
}
